import { type SfyrAnySchema } from './types';

export type SfyrSubscribeCallback = () => void;

export class SfyrSyncDatabase {
  readonly schema: SfyrAnySchema;
  private readonly values: Record<string, any> = {};
  private readonly listeners: Set<SfyrSubscribeCallback> = new Set();
  private version = 0;

  constructor(schema: SfyrAnySchema) {
    this.schema = schema;
  }

  /**
   * Get a value by its fully resolved key.
   * Returns undefined if not found.
   */
  getLocal(key: string): any {
    return this.values[key];
  }

  /**
   * Set a value by its fully resolved key.
   */
  setLocal(key: string, value: any): void {
    const prev = this.values[key];
    if (prev !== value) {
      this.values[key] = value;
      this.version++;
      this.notifyListeners();
    }
  }

  /**
   * Check if a key exists in the database.
   */
  hasLocal(key: string): boolean {
    return key in this.values;
  }

  /**
   * Get all keys in the database.
   */
  getAllKeys(): string[] {
    return Object.keys(this.values);
  }

  /**
   * Get current version (increments on each change).
   * Used for snapshot comparison in useSyncExternalStore.
   */
  getVersion(): number {
    return this.version;
  }

  /**
   * Subscribe to database changes.
   * Returns an unsubscribe function.
   */
  subscribe(callback: SfyrSubscribeCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
